import React, { FC, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import useLocales from 'hooks/useLocales';
import Navbar from 'components/Navbar';
import Image from 'components/image/Image';
import Footer from 'components/Footer/Index';
import Autocomplete from 'components/shared/Autocomplete/Index';
import FormField from 'components/FormField/Index';
import Button from 'components/Button/Index';
import DropDown from 'components/Dropdown/index';
import { COMPANY_DEFAULT_ROUTE } from 'core/constants/companyRoutes';
import { companySizeChoices } from 'core/constants/aboutMePopUpConstants';
import { InputType } from 'core/enum/inputType';
import { JobCitiesResultsType } from 'core/interface/job';
import { IIndustry } from 'core/interface/companies';
import { CompanyProfileReqBody } from 'core/interface/matchProfile';
import { IMe } from 'core/interface/me';
import { RootState } from 'redux/store';
import { SaveCompanies } from '../../redux/slices/company';
import { AuthService } from 'services/auth/auth';
import { CompanyService } from 'services/companes/companies';
import { JobService } from 'services/jobs/job';
import { ProfileService } from 'services/profile/profile';
import {
  getIndustryDisplayName,
  normalizeIndustry
} from 'utils/industry';
import { validationSchema } from './form';
import './Index.scss';

const CompanySettings: FC = (): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useLocales();
  const me = useSelector((state: IMe) => state.user).user;
  const { companies } = useSelector((state: RootState) => state.company);
  const { industry: industries } = useSelector(
    (state: RootState) => state.industries
  );
  const { cities } = useSelector((state: RootState) => state.locations);

  useEffect(() => {
    if (me?.company) {
      CompanyService.getCompanies(me.company);
    }
  }, [me?.company]);

  const handleBack = () => {
    navigate(COMPANY_DEFAULT_ROUTE);
  };

  const getIndustries = async (query?: string): Promise<void> => {
    ProfileService.getIndustries(query);
  };

  const getCities = async (q: string, limit: number = 10) => {
    JobService.getLocations({
      limit,
      q
    });
  };

  const handleLoadMoreCities = () => {
    const params = { limit: 10, offset: cities.length };
    JobService.getLocations(params, true);
  };

  const handleLocationChange = (value: string, setFieldValue: any) => {
    if (!value) {
      setFieldValue('location', null);
      return;
    }

    const selectedCity = cities?.find(
      (city: JobCitiesResultsType) => city.display_name === value
    );
    if (selectedCity) {
      setFieldValue('location', {
        id: selectedCity.id,
        display_name: selectedCity.display_name
      });
    }
  };

  const handleSubmit = async (
    values: CompanyProfileReqBody
  ): Promise<void> => {
    const formattedValues = { ...values };
    if (formattedValues.website) {
      formattedValues.website = formattedValues.website.match(
        /^(https?|ftp):\/\//
      )
        ? formattedValues.website
        : `https://${formattedValues.website}`;
    }

    const requestBody: Record<string, any> = {};

    Object.keys(formattedValues).forEach((key) => {
      if (
        key !== 'location' &&
        key !== 'size' &&
        key !== 'logo' &&
        key !== 'industry'
      ) {
        requestBody[key] = (formattedValues as Record<string, any>)[key];
      }
    });

    const industry = normalizeIndustry(formattedValues.industry, industries);
    if (industry) {
      requestBody.industry = industry;
    }

    if (formattedValues.location) {
      requestBody.location = {
        id: formattedValues.location.id,
        display_name: formattedValues.location.display_name
      };
    }

    if (formattedValues.size) {
      requestBody.size = formattedValues.size;
    }

    try {
      const res = await AuthService.getAccountCompany(me.company, requestBody);

      if (res) {
        dispatch(SaveCompanies(res));
        navigate(COMPANY_DEFAULT_ROUTE);
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className="company-settings-page">
      <Navbar />
      <div className="company-settings-page__content">
        <div className="navigation_section">
          <div className="back_button skillio-back-button" onClick={handleBack}>
            <Image name="arrow-left.svg" />
            {t('Back')}
          </div>
        </div>
        <div className="main_section">
          <div className="company-settings-page__card">
            <div className="company-settings-page__header">
              <h1 className="company-settings-page__title">
                {t('Nice to meet you')}
              </h1>
              <p className="company-settings-page__description">
                {t('Please tell us a little bit more about your company')}
              </p>
            </div>
            <Formik
              enableReinitialize
              initialValues={{
                ...companies,
                industry: normalizeIndustry(companies?.industry, industries),
                location: companies?.location || null,
                size: companies?.size || null
              }}
              onSubmit={handleSubmit}
              validationSchema={validationSchema(t)}
            >
              {({ setFieldValue, isValid, values }) => (
                <Form className="company-settings-page__form">
                  <div className="field">
                    <span className="label">{t('Company name')}</span>
                    <FormField
                      inputClassName="input"
                      fieldName="name"
                      placeholder={t('Company name')}
                      type={InputType.text}
                    />
                  </div>
                  <div className="field">
                    <span className="label">{t('Company website (url)')}</span>
                    <FormField
                      inputClassName="input"
                      fieldName="website"
                      placeholder="https://example.com"
                      type={InputType.text}
                    />
                  </div>
                  <div className="field">
                    <span className="label">{t('Primary contact name')}</span>
                    <FormField
                      inputClassName="input"
                      fieldName="contact_name"
                      placeholder={t('Contact name')}
                      type={InputType.text}
                    />
                  </div>
                  <div className="field">
                    <span className="label">{t('Primary contact email')}</span>
                    <FormField
                      inputClassName="input"
                      fieldName="contact_email"
                      placeholder="contact@email.com"
                      type={InputType.email}
                    />
                  </div>
                  <div className="field">
                    <span className="label">
                      {t('Primary contact job title')}
                    </span>
                    <FormField
                      inputClassName="input"
                      fieldName="contact_job_title"
                      placeholder={t('Job title')}
                      type={InputType.text}
                    />
                  </div>
                  <div className="field">
                    <span className="label">{t('Billing email')}</span>
                    <FormField
                      inputClassName="input"
                      fieldName="billing_email"
                      placeholder="billing@email.com"
                      type={InputType.email}
                    />
                  </div>
                  <div className="field">
                    <span className="label">
                      {t('Official company language')}
                    </span>
                    <DropDown
                      items={[
                        { value: 'en', to_show: 'English' },
                        { value: 'nl', to_show: 'Dutch' }
                      ]}
                      fieldName="lang"
                      selectedValue={(val: string) => setFieldValue('lang', val)}
                      defaultValue={values.lang}
                      placeholder={t('Select language')}
                    />
                  </div>
                  <div className="field">
                    <span className="label">{t('Industry')}</span>
                    <Autocomplete
                      className="auto"
                      fieldName="industry"
                      value={getIndustryDisplayName(values.industry)}
                      options={
                        industries?.map((e) => ({
                          id: e?.id,
                          title: e?.name
                        })) || []
                      }
                      show_location={false}
                      placeholder={t('Industry')}
                      onChange={(e) => {
                        const selectedIndustry = industries?.find(
                          (industry) => industry.name === e
                        );
                        if (selectedIndustry) {
                          const industry: IIndustry = {
                            id: selectedIndustry.id,
                            name: selectedIndustry.name
                          };
                          setFieldValue('industry', industry);
                        }
                      }}
                      onInputChange={(e) => getIndustries(e)}
                      values={industries}
                      onFocus={(e) => {
                        getIndustries(e);
                      }}
                    />
                  </div>
                  <div className="field">
                    <span className="label">
                      {t('In which city is your company located?')}
                    </span>
                    <Autocomplete
                      fieldName="location"
                      className="auto"
                      value={values.location?.display_name || ''}
                      options={
                        cities.map((e: any) => ({
                          id: e?.id,
                          title: e?.display_name
                        })) || []
                      }
                      show_location={true}
                      placeholder={t(
                        "Enter a city name (e.g., 'Paris') to search…"
                      )}
                      onChange={(e) => {
                        handleLocationChange(e, setFieldValue);
                      }}
                      onInputChange={(e) => getCities(e)}
                      values={cities}
                      onFocus={(e) => getCities(e)}
                      loadMore={handleLoadMoreCities}
                    />
                  </div>
                  <div className="field">
                    <span className="label">{t('Company size')}</span>
                    <DropDown
                      dropdownClassName="company_size_dropdown"
                      items={companySizeChoices}
                      fieldName="size"
                      selectedValue={(val: string) =>
                        setFieldValue('size', parseInt(val))
                      }
                      defaultValue={values.size?.toString()}
                      placeholder={t('Select company size')}
                    />
                  </div>
                  <Button
                    type="submit"
                    t={t}
                    title="Save"
                    className={`${isValid ? 'valid' : 'invalid'} save`}
                  />
                  {!isValid && (
                    <div className="company-settings-page__form-error">
                      Form has validation errors. Check required fields.
                    </div>
                  )}
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CompanySettings;
